import { useState, useEffect, useCallback, useRef } from 'react'
import { FilterBuilder } from './FilterBuilder'
import { SpecificUsersPicker } from './SpecificUsersPicker'
import {
  getMailAudiencePresets,
  resolveMailAudience,
  type AudiencePreset,
  type AudienceResolveRequest,
  type AudienceFilter,
  type AudienceUserMatch,
  type AdminMailCampaignKind,
} from '../../services/adminApi'

type AudienceMode = 'preset' | 'custom' | 'users'

interface AudienceSelectorProps {
  /** Called whenever the user changes the audience selection. Passes null while still loading or invalid. */
  onChange: (req: AudienceResolveRequest | null) => void
  kind?: AdminMailCampaignKind
}

export function AudienceSelector({ onChange, kind = 'marketing' }: AudienceSelectorProps) {
  const [mode, setMode] = useState<AudienceMode>('preset')

  // Preset mode
  const [presets, setPresets] = useState<AudiencePreset[]>([])
  const [selectedPresetKey, setSelectedPresetKey] = useState<string>('')
  const [loadingPresets, setLoadingPresets] = useState(true)

  // Custom filter mode
  const [customFilter, setCustomFilter] = useState<AudienceFilter | null>(null)

  // Specific users mode
  const [selectedUsers, setSelectedUsers] = useState<AudienceUserMatch[]>([])

  // Resolved preview
  const [previewCount, setPreviewCount] = useState<number | null>(null)
  const [previewSampled, setPreviewSampled] = useState(false)
  const [previewResolvedAt, setPreviewResolvedAt] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  // Debounce timer ref
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load presets on mount
  useEffect(() => {
    setLoadingPresets(true)
    getMailAudiencePresets()
      .then((res) => {
        if (res.ok && res.data) {
          setPresets(res.data.presets)
          if (res.data.presets.length > 0) {
            setSelectedPresetKey(res.data.presets[0].key)
          }
        }
      })
      .catch(() => console.error('Failed to load audience presets'))
      .finally(() => setLoadingPresets(false))
  }, [])

  // Build the AudienceResolveRequest from current state
  const buildRequest = useCallback((): AudienceResolveRequest | null => {
    if (mode === 'preset') {
      if (!selectedPresetKey) return null
      return { preset_key: selectedPresetKey }
    } else if (mode === 'custom') {
      if (!customFilter) return null
      return { custom_filter: customFilter }
    } else {
      if (selectedUsers.length === 0) return null
      return {
        custom_filter: {
          kind: 'cond',
          field: 'user_id',
          op: 'in',
          value: selectedUsers.map((u) => u.user_id),
        },
      }
    }
  }, [mode, selectedPresetKey, customFilter, selectedUsers])

  // Emit onChange whenever the request changes
  useEffect(() => {
    onChange(buildRequest())
  }, [buildRequest, onChange])

  // Debounced audience preview
  const schedulePreview = useCallback((req: AudienceResolveRequest | null) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    if (!req) {
      setPreviewCount(null)
      setPreviewResolvedAt(null)
      return
    }
    debounceTimer.current = setTimeout(async () => {
      setPreviewLoading(true)
      try {
        const res = await resolveMailAudience(req, kind)
        if (res.ok && res.data) {
          setPreviewCount(res.data.count)
          setPreviewSampled(res.data.sampled)
          setPreviewResolvedAt(res.data.resolved_at)
        } else {
          setPreviewCount(null)
          setPreviewResolvedAt(null)
        }
      } catch {
        setPreviewCount(null)
        setPreviewResolvedAt(null)
      } finally {
        setPreviewLoading(false)
      }
    }, 500)
  }, [kind])

  // Trigger preview whenever the request changes
  useEffect(() => {
    schedulePreview(buildRequest())
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedPresetKey, customFilter, selectedUsers, kind])

  const handleFilterChange = useCallback((filter: AudienceFilter | null) => {
    setCustomFilter(filter)
  }, [])

  const selectedPreset = presets.find((p) => p.key === selectedPresetKey)

  const tabStyles = (active: boolean) =>
    `flex-1 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer select-none ${
      active
        ? 'bg-[#503864] text-white'
        : 'bg-transparent text-gray-600 hover:bg-gray-200 active:bg-gray-300 dark:text-gray-200 dark:hover:bg-white/10 dark:active:bg-white/20'
    }`

  const selectStyles = `w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2.5 text-base transition-all duration-200 focus:border-[#503864] dark:focus:border-[#7f4f9a] focus:outline-none focus:ring-2 focus:ring-[#503864]/20 dark:focus:ring-[#7f4f9a]/20`

  return (
    <div className="flex flex-col gap-3">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Audience <span className="text-red-500">*</span>
      </label>

      {/* Mode tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
        <button
          type="button"
          onClick={() => setMode('preset')}
          className={tabStyles(mode === 'preset')}
        >
          Preset
        </button>
        <button
          type="button"
          onClick={() => setMode('custom')}
          className={tabStyles(mode === 'custom')}
        >
          Custom Filter
        </button>
        <button
          type="button"
          onClick={() => setMode('users')}
          className={tabStyles(mode === 'users')}
        >
          Specific Users
        </button>
      </div>

      {/* Preset mode */}
      {mode === 'preset' && (
        <div>
          {loadingPresets ? (
            <div className="flex items-center justify-center py-4">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#503864] border-t-transparent" />
            </div>
          ) : presets.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No audience presets configured.</p>
          ) : (
            <>
              <select
                value={selectedPresetKey}
                onChange={(e) => setSelectedPresetKey(e.target.value)}
                className={selectStyles}
              >
                {presets.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                    {p.estimated_size_hint !== null && p.estimated_size_hint !== undefined
                      ? ` (~${p.estimated_size_hint.toLocaleString()})`
                      : ''}
                  </option>
                ))}
              </select>
              {selectedPreset?.description && (
                <p className="text-xs text-gray-500 mt-2">{selectedPreset.description}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Custom filter mode */}
      {mode === 'custom' && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <FilterBuilder onChange={handleFilterChange} />
        </div>
      )}

      {/* Specific users mode */}
      {mode === 'users' && (
        <SpecificUsersPicker selected={selectedUsers} onChange={setSelectedUsers} />
      )}

      {/* Recipient count preview */}
      <div className="flex items-center gap-2 text-sm">
        <svg className="h-4 w-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <span className="text-gray-600 dark:text-gray-400">
          {previewLoading ? (
            'Calculating...'
          ) : previewCount !== null ? (
            <>
              <strong>{previewCount.toLocaleString()}</strong> recipients
              {previewSampled && ' (sampled)'}
              {kind === 'service_notice' && ', including people who turned off marketing email'}
              {previewResolvedAt && (
                <span className="text-gray-400 ml-1">
                  · resolved {new Date(previewResolvedAt).toLocaleTimeString()}
                </span>
              )}
            </>
          ) : (
            'Select an audience to preview'
          )}
        </span>
      </div>
    </div>
  )
}
